import React, { useState, memo } from "react"

import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  Typography,
  IconButton,
  ButtonBase
} from "@mui/material"

import ConfirmationDialog from "../../Dialogs/Confirmation"
import EditEscalationModal from "../../Settings/Escalation/EditEscalationModal"

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import SmartToyIcon from "@mui/icons-material/SmartToy"
import MessageIcon from "@mui/icons-material/Message"
import GroupIcon from "@mui/icons-material/Group"
import CompareArrowsIcon from "@mui/icons-material/CompareArrows"
import DeleteIcon from "@mui/icons-material/Delete"
import CloseIcon from "@mui/icons-material/Close"

import { getSession } from "../../../auth"

import api from "../../../api"
import useNotification from "../../Notification"

import "./styles.css"

const EscalationDialog = memo(
  ({ onClose, type, data, getEscalation, builders }) => {
    const [toDelete, setToDelete] = useState(null)
    const [toUpdate, setToUpdate] = useState(null)
    const [showCreateEscalation, setShowCreateEscalation] = useState(false)

    const allQueues = getSession()?.profile?.enterprise?.queues

    const deleteEscalation = async (data) => {
      const enterpriseId = getSession()?.profile?.enterprise?.id
      if (!enterpriseId)
        return
      const response = await api.delete(`api/enterprises/${enterpriseId}/escalation/${data.id}/`)
      if (response.status === 204) {
        await getEscalation()
        setToDelete(null)
        useNotification("Sucesso", "Automação removida com sucesso.", "success")
        return
      }
      useNotification("Ops!", "Algo deu errado, tente novamente em alguns instantes.", "danger")
    }

    return (
      <>
        {showCreateEscalation &&
          <EditEscalationModal
            data={{}}
            allQueues={allQueues.reduce((acc, row) => {
              acc[row.name] = {
                name: row.description,
                checked: false
              }
              return acc
            }, {})}
            allChatBots={builders.reduce((acc, row) => {
              acc[row.id] = {
                name: row.name,
                checked: false
              }
              return acc
            }, {})}
            rows={[]}
            setParentState={(data) => {
              getEscalation()
              setShowCreateEscalation(false)
            }}
            handleClose={() => setShowCreateEscalation(false)}
            forcedType={type}
            method="POST"
          />
        }
        {toDelete &&
          <ConfirmationDialog 
            open={true}
            handleClose={() => setToDelete(false)}
            title={'Confirmação'}
            description={<>Tem certeza que deseja remover a automação "{toDelete.description}"?</>}
            handleSubmit={() => deleteEscalation(toDelete)}
          />
        }
        {toUpdate &&
          <EditEscalationModal
            data={toUpdate}
            allQueues={allQueues.reduce((acc, row) => {
              const checkedIds = (toUpdate)
                ? new Set(toUpdate?.queues?.map(r => r.queue) ?? [])
                : new Set()
              acc[row.name] = {
                name: row.description,
                checked: checkedIds.has(row.name)
              }
              return acc
            }, {})}
            allChatBots={builders.reduce((acc, row) => {
              const checkedIds = (toUpdate)
                ? new Set(toUpdate?.chatbots?.map(r => r.chatbot_id) ?? [])
                : new Set()
              acc[row.id] = {
                name: row.name,
                checked: checkedIds.has(row.id)
              }
              return acc
            }, {})}
            rows={data}
            setParentState={(data) => {
              getEscalation()
              setToUpdate(null)
            }}
            handleClose={() => setToUpdate(null)}
            forcedType={type}
            method="PATCH"
          />
        }
        <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
            Configurações
            <Button sx={{ minWidth: "2rem", padding: 0 }} onClick={onClose}>
              <CloseIcon />
            </Button>
          </DialogTitle>
          <DialogContent>
            <Box display="grid" gridTemplateColumns="repeat(auto-fill, 270px)" gap={2}>
              {data.map((row) => (
                <ButtonBase key={row.id} onClick={() => setToUpdate(row)} className="escalation-button-base">
                  <IconButton className="delete-btn" onClick={(event) => {
                    event.stopPropagation()
                    setToDelete(row)
                  }}>
                    <DeleteIcon fontSize="small" sx={{ color: "#c95f50" }} />
                  </IconButton>
                  <Typography className="description-row" variant="h6">
                    {row.description}
                  </Typography>
                  <Box mt={1}>
                    <Typography display="flex" gap={1} alignItems="center" mb={1}>
                      {(type === "chatbots") ? <SmartToyIcon fontSize="small" /> : <GroupIcon fontSize="small" />}
                      {(type === "chatbots") ? "ChatBots" : "Filas"}
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block", marginLeft: ".5rem" }}>
                      {row[type]?.map((entity) => {
                        if (entity.chatbot_id)
                          return builders.find((b) => b.id === entity.chatbot_id)?.name
                        if (entity.queue)
                          return allQueues.find((q) => q.name === entity.queue)?.description
                        return null
                      }).filter(Boolean).join(", ")}
                    </Typography>
                  </Box>
                  {(row.messages?.length > 0) &&
                    <Box>
                      <Typography display="flex" gap={1} alignItems="center" mb={1}>
                        <MessageIcon fontSize="small" />
                        Mensagens
                      </Typography>
                      {row.messages?.map((message, index) => (
                        <Typography key={index} variant="caption" sx={{ display: "block", marginLeft: ".5rem" }} className="description-row">
                          {`${message.timeout/60} min: ${message.message}`}
                        </Typography>
                      ))}
                    </Box>
                  }
                  {(row.destination_queue) &&
                    <Box>
                      <Typography display="flex" gap={1} alignItems="center" mb={1}>
                        <CompareArrowsIcon fontSize="small" />
                        Transbordo
                      </Typography>
                      <Typography variant="caption" sx={{ display: "block", marginLeft: ".5rem" }} className="description-row">
                        {`${row.timeout/60} min: ${allQueues.find((q) => q.name === row.destination_queue)?.description}`}
                      </Typography>
                    </Box>
                  }
                </ButtonBase>
              ))}
              <ButtonBase onClick={() => setShowCreateEscalation(type)} className="escalation-button-base create">
                <AddCircleOutlineIcon className="add-button" />
              </ButtonBase>
            </Box>
          </DialogContent>
        </Dialog>
      </>
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.type === nextProps.type &&
      prevProps.data === nextProps.data
    )
  }
)

export default EscalationDialog
